import ConfirmModal from '@/components/ConfirmModal';
import { Link } from '@/components/primitives/Link';
import {
  salesPointConfigurationSelector,
  tenantSelector,
} from '@/features/configuration/configurationSelector';
import { cancelBookingLoadingSelector } from '@/features/loading/loadingSelectors';
import {
  deletePurchaseFlowBooking,
  resetPurchase,
} from '@/features/purchase/purchaseActions';
import { purchaseFlowBookingSelector } from '@/features/purchase/purchaseSelectors';
import { useTenantConfiguration } from '@/hooks/useTenantConfiguration';
import { TransText } from '@/i18n/trans/text';
import { persistor } from '@/store';
import { useDispatch, useSelector } from '@/store/utils';
import { Icons, Typography } from '@packages/webshop-design-system';
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const Logo = ({ clickable = true }: { clickable?: boolean }) => {
  const dispatch = useDispatch();
  const selectedTenant = useSelector(tenantSelector);
  const isCancellingBooking = useSelector(cancelBookingLoadingSelector);
  const tenantConfiguration = useTenantConfiguration();
  const logoUrl = useSelector(salesPointConfigurationSelector)
    ?.organizationBranding?.logoUrl;

  // Render the API-provided branding logo when present, else the Turnit default.
  const TurnitLogo = Icons.turnit;
  const logo = logoUrl ? (
    <img
      src={logoUrl}
      alt={selectedTenant.label}
      className="h-10 w-auto max-w-[180px] object-contain"
    />
  ) : (
    <TurnitLogo />
  );
  const location = useLocation();
  const userIsInCheckout = location.pathname === '/purchase/checkout';
  const booking = useSelector(purchaseFlowBookingSelector);

  const handleRedirectionToConfiguredUrl = useCallback(async () => {
    await dispatch(deletePurchaseFlowBooking(booking!.id!)).unwrap();
    dispatch(resetPurchase());
    void persistor.purge();
    window.location.assign(tenantConfiguration!.webPageUrl!);
  }, [booking, tenantConfiguration, dispatch]);

  if (!tenantConfiguration?.webPageUrl || !clickable) {
    return logo;
  }

  if (tenantConfiguration.webPageUrl && userIsInCheckout) {
    return (
      <ConfirmModal
        title={<TransText i18nKey="cancelPurchase" />}
        description={
          <Typography variant="paragraph">
            <TransText i18nKey="areYouSureYouWantToCancelThePurchase" />
          </Typography>
        }
        cancelLabel="dismiss"
        confirmLabel="confirm"
        onConfirm={handleRedirectionToConfiguredUrl}
        isConfirmLoading={isCancellingBooking}
      >
        <div className="cursor-pointer text-dark hover:text-primary-dark focus-visible:outline-tint">
          {logo}
        </div>
      </ConfirmModal>
    );
  }

  return (
    <Link to={tenantConfiguration?.webPageUrl} aria-label="Hero">
      {logo}
    </Link>
  );
};

export default Logo;
